import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatInTimeZone } from 'date-fns-tz';
import { ptBR } from 'date-fns/locale';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { STATUS_LABELS, type PublicAppointmentView } from '@barbeiropro/contracts';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { HttpError } from '@/lib/api';
import { money } from '@/lib/format';
import { Wrap } from './booking-page';
import { useDocumentMeta } from './public-site-root';
import { pub } from './pub-api';
import { displayStyle } from './theme';

export function ManagePage({ slug, token }: { slug: string; token: string }) {
  useDocumentMeta('Seu agendamento');
  const qc = useQueryClient();
  const key = ['site-public', slug, 'appointment', token] as const;
  const q = useQuery({ queryKey: key, queryFn: () => pub<PublicAppointmentView>('GET', `/public/${slug}/appointments/${token}`), retry: false, enabled: Boolean(token) });
  const cancel = useMutation({
    mutationFn: () => pub('POST', `/public/${slug}/appointments/${token}/cancel`, {}),
    onSuccess: () => {
      toast.success('Agendamento cancelado');
      qc.invalidateQueries({ queryKey: key });
    },
  });

  if (q.isPending && token) return <Wrap><p role="status" style={{ color: 'var(--s-muted)' }}>Carregando…</p></Wrap>;
  if (q.isError || !q.data) {
    const nf = !token || (q.error instanceof HttpError && q.error.status === 404);
    return (
      <Wrap>
        <h1 className="text-5xl" style={displayStyle}>{nf ? 'Agendamento não encontrado' : 'Não foi possível carregar'}</h1>
        <p className="mt-4 max-w-md">{nf ? 'Confira se o link está completo. Ele foi mostrado ao final do agendamento e enviado por e-mail, quando ativo.' : 'Verifique a conexão e tente de novo.'}</p>
        {!nf && <button className="mt-4 font-semibold underline" onClick={() => q.refetch()}>Tentar de novo</button>}
      </Wrap>
    );
  }

  const a = q.data;
  const when = (iso: string) => formatInTimeZone(iso, a.timezone, "EEEE, d 'de' MMMM 'às' HH:mm", { locale: ptBR });
  const cancelled = a.status === 'cancelled';

  async function doCancel() {
    try {
      await cancel.mutateAsync();
    } catch (e) {
      if (e instanceof HttpError && e.code === 'cancellation_too_late')
        toast.error(`O prazo para cancelar pelo site já passou (era até ${when(a.cancellationDeadline)}). Fale com a barbearia.`);
      else toast.error(e instanceof HttpError ? e.message : 'Não foi possível cancelar. Tente de novo.');
      throw e;
    }
  }

  return (
    <Wrap>
      <h1 className="text-[clamp(2.5rem,9cqw,4.5rem)]" style={displayStyle}>{cancelled ? 'Agendamento cancelado' : `Olá, ${a.customerFirstName}`}</h1>
      <p className="mt-2 text-sm font-semibold" style={{ color: 'var(--s-muted)' }}>Situação: {STATUS_LABELS[a.status]}</p>
      <dl className="mt-8 grid max-w-lg gap-4 border-y-2 py-6" style={{ borderColor: 'var(--s-ink)' }}>
        {(
          [
            ['Serviço', a.serviceName],
            ['Profissional', a.barberName],
            ['Quando', when(a.startsAt)],
            ['Onde', `${a.branchName} · ${a.branchAddress.street}, ${a.branchAddress.number}`],
            ['Valor', money(a.priceCents)],
          ] as const
        ).map(([k, v]) => (
          <div key={k}>
            <dt className="text-xs font-semibold" style={{ color: 'var(--s-muted)' }}>{k}</dt>
            <dd className="text-lg">{v}</dd>
          </div>
        ))}
      </dl>
      {!cancelled && (
        <div className="mt-8 max-w-lg">
          {a.canCancel ? (
            <>
              <p className="mb-3 text-sm" style={{ color: 'var(--s-muted)' }}>Você pode cancelar até {when(a.cancellationDeadline)}.</p>
              <ConfirmDialog
                trigger={<button type="button" className="inline-flex min-h-12 items-center border-2 px-7 font-semibold" style={{ borderColor: 'var(--s-ink)' }}>Cancelar agendamento</button>}
                title="Cancelar este agendamento?"
                description={`${a.serviceName} com ${a.barberName}, ${when(a.startsAt)}. O horário volta a ficar livre para outras pessoas.`}
                confirmLabel="Cancelar agendamento"
                onConfirm={doCancel}
              />
            </>
          ) : (
            <p className="border-2 p-4" style={{ borderColor: 'var(--s-line)' }}>
              O prazo para cancelar pelo site terminou ({when(a.cancellationDeadline)}). Para alterar ou cancelar, fale diretamente com a barbearia.
            </p>
          )}
        </div>
      )}
      <Link to={`/b/${slug}`} className="mt-10 inline-block font-semibold underline underline-offset-4">Voltar ao site</Link>
    </Wrap>
  );
}
